"use client";

import { Check } from "lucide-react";
import Image from "next/image";
import { useState } from "react";

import { ShotFigure } from "@/components/product-shot/figure";
import { RECEIVABLES_FIGURE, ReceivablesWindow } from "@/components/product-shot/receivables-shot";
import { DATA_LABEL_CLS, DISPLAY } from "@/components/editorial";
import { UkMark } from "@/components/editorial/marks";
import { AppSurface } from "@/components/editorial/product-bridge/app-surface";
import frame from "@/components/editorial/shot-frame.module.css";
import { useTimeline } from "@/components/moments/use-timeline";

import styles from "./landing-model-choice.module.css";

/*
 * After the UK story: the Twin is not tied to one model. The product's own
 * answer (the receivables session the site shows) with its model menu open:
 * Venus, the private model, or the providers teams already use. The answer
 * behind the menu does not change, whichever model is chosen. In the site's
 * own words: requests go to the AI provider configured for your
 * organisation (the trust band); your Twin can answer through an external
 * provider or through Venus, under the same rules for sources and access
 * (/research). Venus already serves, privately, from dedicated UK or EU
 * infrastructure (/research; Archie, 2026-10-01): what is in development is
 * the fully UK-hosted Twin of the band above, not the model.
 */

const INK_72 = "text-[color-mix(in_srgb,var(--theme-ink)_72%,transparent)]";
const INK_88 = "text-[color-mix(in_srgb,var(--theme-ink)_88%,transparent)]";

type Model = { id: string; label: string; family: string; icon?: string; where: string };

const MODELS: readonly Model[] = [
  { id: "venus", label: "Venus", family: "Private model", where: "Runs privately in the UK or EU" },
  { id: "openai", label: "OpenAI", family: "GPT", icon: "/concepts/source-icons/chatgpt.svg", where: "Processed by the provider" },
  { id: "anthropic", label: "Anthropic", family: "Claude", icon: "/concepts/source-icons/claude.svg", where: "Processed by the provider" },
  { id: "google", label: "Google", family: "Gemini", icon: "/concepts/source-icons/gemini.svg", where: "Processed by the provider" },
];

function ModelMark({ model }: { model: Model }) {
  if (!model.icon) return <UkMark />;
  return <Image src={model.icon} alt="" width={16} height={16} className="size-4 shrink-0 object-contain" />;
}

const CYCLE = MODELS.map(() => 2600);

/** The composer's model menu, open, in the application's popover recipe; it cycles until a model is chosen. */
function ModelMenu() {
  const cycled = useTimeline(CYCLE, { restStep: 0 });
  const [chosen, setChosen] = useState<number | null>(null);
  const selected = MODELS[chosen ?? cycled] ?? MODELS[0]!;
  return (
    <AppSurface
      tone="light"
      // The product's own popover, laid over a shot: its edge keeps the corners a shot's window has.
      data-frame-corners=""
      className="rounded-lg border border-[hsl(var(--border))] p-1 text-sm shadow-[0_18px_40px_-16px_rgb(0_0_0/0.35),0_2px_6px_rgb(0_0_0/0.06)]"
      style={{ backgroundColor: "hsl(var(--popover))" }}
    >
      <p className="px-2 pt-1.5 pb-1 text-xs font-medium text-[hsl(var(--muted-foreground))]">Model</p>
      <div role="radiogroup" aria-label="Model">
        {MODELS.map((model, index) => {
          const on = model.id === selected.id;
          return (
            <button
              key={model.id}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => setChosen(index)}
              className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-[hsl(var(--muted))] ${
                on ? "bg-[hsl(var(--muted))]" : ""
              }`}
            >
              <ModelMark model={model} />
              <span className="font-medium">{model.label}</span>
              <span className="text-[hsl(var(--muted-foreground))]">{model.family}</span>
              {on ? <Check aria-hidden className="ml-auto size-4 text-[hsl(var(--primary))]" /> : null}
            </button>
          );
        })}
      </div>
      <p className="mt-1 border-t border-[hsl(var(--border))] px-2 pt-2 pb-1.5 text-xs text-[hsl(var(--muted-foreground))]">{selected.where}</p>
    </AppSurface>
  );
}

/**
 * Your choice of model: the product's answer, and the menu that picks who
 * writes it. A band on its page's rail, straight after the UK story.
 */
export function ModelChoice({ look = "editorial" }: { look?: "editorial" | "app" }) {
  const app = look === "app";
  return (
    <section className={`border-b py-20 ${app ? "border-[hsl(var(--border))]" : "border-dotted border-[var(--rule)]"}`}>
      <div className="grid items-center gap-14 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
        <div>
          <p
            className={
              app ? "text-[11px] font-medium tracking-[0.06em] text-[hsl(var(--primary))] uppercase" : `${DATA_LABEL_CLS} text-[var(--theme-accent-text)]`
            }
          >
            Your choice of model
          </p>
          <h2 style={DISPLAY} className={`${INK_88} mt-4 max-w-[17ch] text-[clamp(2rem,3.6vw,3rem)] leading-[1.04] font-semibold tracking-[-0.03em]`}>
            Or use the models you already know.
          </h2>
          <p className={`mt-5 max-w-[46ch] ${INK_72} text-[1.02rem] leading-[1.65]`}>
            Your Twin can answer through Venus, our private model, or through OpenAI, Anthropic or Google. Whichever you choose, it works from the same
            records and definitions, and each checked answer shows its sources.
          </p>
          <p className={`mt-4 text-[0.88rem] ${INK_72}`}>Requests go to the model configured for your organisation.</p>
        </div>
        <div className="min-w-0">
          <ShotFigure {...RECEIVABLES_FIGURE}>
            {/* The menu is placed against the frame itself, not the figure,
                so it opens over the composer at every width, whatever the
                caption under the frame wraps to. */}
            <div className="relative">
              <ReceivablesWindow className={frame.cropped} />
              <div className={styles.menu}>
                <ModelMenu />
              </div>
            </div>
          </ShotFigure>
        </div>
      </div>
    </section>
  );
}
